export interface TemplateContent {
  title: string;
  html: string;
}

const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
const year = new Date().getFullYear();
const dueDate = new Date(Date.now() + 30 * 86400000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const baseStyles = `
  body { font-family: Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 40px; font-size: 13px; line-height: 1.6; }
  h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
  h2 { font-size: 15px; font-weight: 600; border-bottom: 2px solid #1a1a1a; padding-bottom: 6px; margin-top: 28px; margin-bottom: 12px; }
  h3 { font-size: 13px; font-weight: 600; margin: 0 0 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  th { background: #f3f4f6; text-align: left; padding: 8px 10px; font-size: 12px; border: 1px solid #e5e7eb; }
  td { padding: 8px 10px; border: 1px solid #e5e7eb; font-size: 12px; vertical-align: top; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; border-bottom: 3px solid #1a1a1a; padding-bottom: 20px; }
  .company { font-size: 11px; color: #6b7280; }
  .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
  .value { font-size: 13px; font-weight: 600; }
  .highlight { background: #f9fafb; padding: 14px; border-left: 3px solid #1a1a1a; border-radius: 0 4px 4px 0; margin: 12px 0; }
  .sig-block { margin-top: 40px; display: flex; gap: 60px; }
  .sig-line { border-top: 1px solid #1a1a1a; padding-top: 6px; width: 220px; font-size: 11px; color: #6b7280; }
  .total-row td { font-weight: 700; background: #f3f4f6; }
  .badge { display: inline-block; background: #1a1a1a; color: #fff; padding: 3px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; }
`;

export const TEMPLATE_CONTENT: Record<string, TemplateContent> = {
  '1': {
    title: 'Professional Invoice',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Professional Invoice</title><style>${baseStyles}
      .invoice-num { font-size: 28px; font-weight: 800; color: #1a1a1a; }
    </style></head><body>
    <div class="header">
      <div>
        <div class="invoice-num">INVOICE</div>
        <div style="font-size:11px;color:#6b7280;margin-top:4px;">{{INVOICE_NUMBER}}</div>
      </div>
      <div style="text-align:right">
        <h3 style="font-size:16px;margin:0 0 4px">{{YOUR_COMPANY}}</h3>
        <div class="company">{{YOUR_ADDRESS}}<br>{{YOUR_EMAIL}}<br>{{YOUR_PHONE}}</div>
      </div>
    </div>
    <div style="display:flex;gap:48px;margin-bottom:28px">
      <div><div class="label">Bill To</div><h3>{{CLIENT_NAME}}</h3><div class="company">{{CLIENT_ADDRESS}}<br>{{CLIENT_EMAIL}}</div></div>
      <div><div class="label">Invoice Date</div><div class="value">{{INVOICE_DATE}}</div></div>
      <div><div class="label">Due Date</div><div class="value">{{DUE_DATE}}</div></div>
      <div><div class="label">Status</div><div><span class="badge">UNPAID</span></div></div>
    </div>
    <h2>Services Rendered</h2>
    <table>
      <tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Amount</th></tr>
      {{INVOICE_ROWS}}
      <tr class="total-row"><td colspan="3" style="text-align:right">Subtotal</td><td>{{SUBTOTAL}}</td></tr>
      <tr><td colspan="3" style="text-align:right;color:#6b7280">Tax ({{TAX_RATE_DISPLAY}})</td><td>{{TAX_AMOUNT}}</td></tr>
      <tr class="total-row"><td colspan="3" style="text-align:right">TOTAL DUE</td><td>{{TOTAL_DUE}}</td></tr>
    </table>
    <h2>Payment Information</h2>
    <div class="highlight">{{PAYMENT_INFO}}</div>
    <p style="font-size:11px;color:#6b7280;margin-top:20px">Payment is due within 30 days. Late payments are subject to a 1.5% monthly fee. Thank you for your business!</p>
    </body></html>`,
  },

  '2': {
    title: 'Service Contract',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Service Contract</title><style>${baseStyles}</style></head><body>
    <div class="header">
      <div><h1>SERVICE AGREEMENT</h1><div style="font-size:11px;color:#6b7280">Contract #{{CONTRACT_NUMBER}} &nbsp;|&nbsp; Effective: {{CONTRACT_DATE}}</div></div>
      <div style="text-align:right;font-size:11px;color:#6b7280">{{PROVIDER_NAME}}<br>{{PROVIDER_ADDRESS}}<br>{{PROVIDER_EMAIL}}</div>
    </div>
    <p>This Service Agreement ("Agreement") is entered into as of <b>{{CONTRACT_DATE}}</b> between <b>{{PROVIDER_NAME}}</b> ("Service Provider") and <b>{{CLIENT_NAME}}</b> ("Client").</p>
    <h2>1. Scope of Services</h2>
    <p>Service Provider agrees to perform the following services: {{SERVICES_DESC}}</p>
    <h2>2. Term</h2>
    <p>This Agreement commences on {{CONTRACT_DATE}} and continues for a period of <b>{{CONTRACT_TERM}}</b>, unless terminated earlier in accordance with this Agreement.</p>
    <h2>3. Compensation</h2>
    <div class="highlight"><b>Monthly Retainer:</b> {{MONTHLY_RATE}}, due on the 1st of each month.<br><b>Project-Based Work:</b> Billed separately at {{HOURLY_RATE}} with prior written approval.</div>
    <h2>4. Confidentiality</h2>
    <p>Each party agrees to keep confidential all proprietary information and not to disclose it to any third party without prior written consent during and for 3 years after termination.</p>
    <h2>5. Intellectual Property</h2>
    <p>Upon full payment, all deliverables become the exclusive property of the Client. Service Provider retains the right to display work in portfolio unless restricted in writing.</p>
    <h2>6. Termination</h2>
    <p>Either party may terminate with <b>30 days written notice</b>. Client owes payment for all work completed up to the termination date.</p>
    <h2>7. Limitation of Liability</h2>
    <p>Service Provider's total liability shall not exceed fees paid in the three months preceding the claim.</p>
    <div class="sig-block">
      <div><div class="sig-line">Service Provider Signature</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Name: _________________ &nbsp; Date: _______</div></div>
      <div><div class="sig-line">Client Signature</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Name: _________________ &nbsp; Date: _______</div></div>
    </div>
    </body></html>`,
  },

  '3': {
    title: 'Modern Resume',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Resume</title><style>${baseStyles}
    .name { font-size: 28px; font-weight: 800; letter-spacing: -0.5px; }
    .contact { font-size: 11px; color: #6b7280; display: flex; gap: 16px; flex-wrap: wrap; margin-top: 6px; }
    .two-col { display: grid; grid-template-columns: 1fr 280px; gap: 32px; margin-top: 24px; }
    .job-title { font-weight: 600; font-size: 13px; }
    .job-meta { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
    .skill-tag { display: inline-block; background: #f3f4f6; border: 1px solid #e5e7eb; padding: 3px 9px; border-radius: 12px; font-size: 11px; margin: 2px 2px 2px 0; }
    </style></head><body>
    <div style="border-bottom:3px solid #1a1a1a;padding-bottom:16px">
      <div class="name">{{FULL_NAME}}</div>
      <div style="font-size:14px;color:#6b7280;margin-top:2px">{{JOB_TITLE}}</div>
      <div class="contact">
        <span>{{EMAIL}}</span><span>{{PHONE}}</span><span>{{LOCATION}}</span><span>{{LINKEDIN}}</span><span>{{GITHUB}}</span>
      </div>
    </div>
    <div class="two-col">
      <div>
        <h2>Experience</h2>
        <div style="margin-bottom:16px">
          <div class="job-title">{{JOB1_TITLE}} — {{JOB1_COMPANY}}</div>
          <div class="job-meta">{{JOB1_DATES}}</div>
          <ul style="margin:4px 0;padding-left:18px;font-size:12px">
            {{JOB1_BULLETS}}
          </ul>
        </div>
        <div style="margin-bottom:16px">
          <div class="job-title">Previous Role — Previous Company</div>
          <div class="job-meta">Add more experience as needed</div>
          <ul style="margin:4px 0;padding-left:18px;font-size:12px">
            <li>Key accomplishment or responsibility here</li>
            <li>Another achievement with measurable results</li>
          </ul>
        </div>
      </div>
      <div>
        <h2>Skills</h2>
        <div>{{SKILLS}}</div>
        <h2>Education</h2>
        <div style="font-size:12px"><b>{{EDUCATION}}</b><br>{{EDUCATION_DATES}}</div>
        <h2>Languages</h2>
        <div style="font-size:12px">English (Native)</div>
      </div>
    </div>
    </body></html>`,
  },

  '4': {
    title: 'NDA Agreement',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Non-Disclosure Agreement</title><style>${baseStyles}</style></head><body>
    <div class="header">
      <div><h1>NON-DISCLOSURE AGREEMENT</h1><div style="font-size:11px;color:#6b7280">Confidential &nbsp;|&nbsp; {{AGREEMENT_DATE}}</div></div>
    </div>
    <p>This Non-Disclosure Agreement ("Agreement") is entered into as of <b>{{AGREEMENT_DATE}}</b> between <b>{{DISCLOSING_PARTY}}</b> ("Disclosing Party / Company") and <b>{{RECEIVING_PARTY}}</b> ("Receiving Party / Recipient").</p>
    <p><b>Purpose:</b> {{PURPOSE}}</p>
    <h2>1. Definition of Confidential Information</h2>
    <p>"Confidential Information" means any and all information disclosed by Company to Recipient including, without limitation, business plans, financial data, customer lists, technical specifications, trade secrets, inventions, source code, product concepts, and any other proprietary information designated as confidential.</p>
    <h2>2. Obligations of Recipient</h2>
    <p>Recipient agrees to: (a) hold all Confidential Information in strict confidence; (b) not disclose any Confidential Information to third parties; (c) not use Confidential Information for any purpose other than the stated purpose above; (d) use at least the same degree of care to protect Confidential Information as it uses to protect its own.</p>
    <h2>3. Exclusions</h2>
    <p>This Agreement does not apply to information that: (a) was publicly known prior to disclosure; (b) becomes publicly known through no breach by Recipient; (c) was independently developed without use of Confidential Information; (d) is required to be disclosed by law or court order.</p>
    <h2>4. Term</h2>
    <div class="highlight">This Agreement is effective for <b>{{NDA_TERM}}</b> from the date of execution. Obligations survive termination for an additional <b>{{SURVIVAL_TERM}}</b>.</div>
    <h2>5. Return of Information</h2>
    <p>Upon request, Recipient shall promptly return or destroy all Confidential Information and certify such destruction in writing.</p>
    <h2>6. Governing Law</h2>
    <p>This Agreement shall be governed by the laws of the State of <b>{{GOVERNING_STATE}}</b>, without regard to conflict of law provisions.</p>
    <h2>7. Remedies</h2>
    <p>Breach of this Agreement may cause irreparable harm, entitling the disclosing party to seek injunctive relief in addition to other legal remedies.</p>
    <div class="sig-block">
      <div><div class="sig-line">{{DISCLOSING_PARTY}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Name: _________________ &nbsp; Title: _________<br>Date: _______</div></div>
      <div><div class="sig-line">{{RECEIVING_PARTY}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Name: _________________ &nbsp; Title: _________<br>Date: _______</div></div>
    </div>
    </body></html>`,
  },

  '5': {
    title: 'Employee Offer Letter',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Offer Letter</title><style>${baseStyles}</style></head><body>
    <div class="header">
      <div><h1>OFFER OF EMPLOYMENT</h1></div>
      <div style="text-align:right;font-size:11px;color:#6b7280">{{COMPANY_NAME}}<br>{{COMPANY_ADDRESS}}<br>{{HR_EMAIL}}</div>
    </div>
    <p>{{OFFER_DATE}}</p>
    <p>Dear <b>{{CANDIDATE_NAME}}</b>,</p>
    <p>We are delighted to offer you a position at <b>{{COMPANY_NAME}}</b>. We believe your skills and experience will make a valuable contribution to our team. Below are the details of your offer:</p>
    <h2>Position Details</h2>
    <table>
      <tr><td style="width:200px"><b>Position</b></td><td>{{POSITION_TITLE}}</td></tr>
      <tr><td><b>Department</b></td><td>{{DEPARTMENT}}</td></tr>
      <tr><td><b>Reports To</b></td><td>{{MANAGER_NAME}}</td></tr>
      <tr><td><b>Start Date</b></td><td>{{START_DATE}}</td></tr>
      <tr><td><b>Employment Type</b></td><td>Full-Time, Exempt</td></tr>
      <tr><td><b>Work Location</b></td><td>{{WORK_LOCATION}}</td></tr>
    </table>
    <h2>Compensation &amp; Benefits</h2>
    <div class="highlight">
      <b>Base Salary:</b> {{BASE_SALARY}} per year, paid bi-weekly<br>
      <b>Signing Bonus:</b> {{SIGNING_BONUS}}<br>
      <b>Annual Bonus:</b> Up to {{BONUS_PERCENT}} of base salary, performance-based
    </div>
    <h2>Benefits Package</h2>
    <p>Medical, dental, and vision insurance (company pays 90%); 401(k) with 4% match; unlimited PTO; $2,000 annual learning budget; home office stipend; 16 weeks parental leave.</p>
    <h2>Conditions of Employment</h2>
    <p>This offer is contingent upon: (a) satisfactory background check; (b) verification of employment eligibility; (c) signing the Company's standard Employee Invention Assignment Agreement.</p>
    <p>This offer expires on <b>{{OFFER_EXPIRY}}</b>. Please sign and return to confirm acceptance.</p>
    <p>We look forward to welcoming you to the {{COMPANY_NAME}} family!</p>
    <div class="sig-block">
      <div><div class="sig-line">Authorized by HR</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Name: _________________ &nbsp; Date: _______</div></div>
      <div><div class="sig-line">Candidate Acceptance</div><div style="font-size:11px;color:#6b7280;margin-top:4px">{{CANDIDATE_NAME}} &nbsp; Date: _______</div></div>
    </div>
    </body></html>`,
  },

  '6': {
    title: 'Project Checklist',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Project Checklist</title><style>${baseStyles}
    .check { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #1a1a1a; border-radius: 2px; margin-right: 8px; vertical-align: middle; }
    .phase { background: #f3f4f6; padding: 6px 12px; font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; border-left: 3px solid #1a1a1a; margin: 20px 0 8px; }
    .item { display: flex; align-items: flex-start; padding: 6px 4px; border-bottom: 1px solid #f3f4f6; font-size: 12px; }
    .priority { font-size: 10px; padding: 1px 7px; border-radius: 10px; margin-left: auto; white-space: nowrap; }
    .high { background: #fee2e2; color: #dc2626; }
    .med { background: #fef9c3; color: #ca8a04; }
    .low { background: #dcfce7; color: #16a34a; }
    </style></head><body>
    <div class="header">
      <div><h1>PROJECT CHECKLIST</h1><div style="font-size:11px;color:#6b7280">{{PROJECT_NAME}} &nbsp;|&nbsp; {{PROJECT_DATE}}</div></div>
      <div style="text-align:right;font-size:11px;color:#6b7280">Project Manager: {{PROJECT_MANAGER}}<br>Target Launch: {{TARGET_DATE}}</div>
    </div>
    <div style="display:flex;gap:32px;margin-bottom:20px">
      <div><span class="label">Total Tasks</span><br><span class="value">24</span></div>
      <div><span class="label">Completed</span><br><span class="value">0</span></div>
      <div><span class="label">In Progress</span><br><span class="value">0</span></div>
      <div><span class="label">Completion</span><br><span class="value">0%</span></div>
    </div>
    <div class="phase">Phase 1 — Discovery &amp; Planning</div>
    ${['Define project scope and objectives', 'Identify key stakeholders', 'Conduct competitive analysis', 'Create project timeline and milestones', 'Confirm budget allocation'].map((t, i) => `<div class="item"><span class="check"></span>${t}<span class="priority ${i < 2 ? 'high' : i < 4 ? 'med' : 'low'}">${i < 2 ? 'High' : i < 4 ? 'Medium' : 'Low'}</span></div>`).join('')}
    <div class="phase">Phase 2 — Design</div>
    ${['Create wireframes for all key pages', 'Design style guide and component library', 'Build high-fidelity mockups', 'Conduct usability testing', 'Obtain stakeholder approval on designs'].map((t, i) => `<div class="item"><span class="check"></span>${t}<span class="priority ${i < 1 ? 'high' : i < 3 ? 'med' : 'low'}">${i < 1 ? 'High' : i < 3 ? 'Medium' : 'Low'}</span></div>`).join('')}
    <div class="phase">Phase 3 — Development</div>
    ${['Set up dev environment and version control', 'Build frontend components', 'Integrate CMS and backend', 'Connect third-party services', 'QA and cross-browser testing', 'Performance optimization'].map((t, i) => `<div class="item"><span class="check"></span>${t}<span class="priority ${i < 2 ? 'high' : 'med'}">${i < 2 ? 'High' : 'Medium'}</span></div>`).join('')}
    <div class="phase">Phase 4 — Launch &amp; Post-Launch</div>
    ${['Final content review and proofread', 'SEO audit and meta tags', 'Deploy to production', 'DNS cutover and SSL verification', 'Monitor analytics for 7 days', 'Post-launch retrospective'].map((t, i) => `<div class="item"><span class="check"></span>${t}<span class="priority ${i < 3 ? 'high' : 'low'}">${i < 3 ? 'High' : 'Low'}</span></div>`).join('')}
    <p style="margin-top:24px;font-size:11px;color:#6b7280">Notes: _______________________________________________________________________________________________________________</p>
    </body></html>`,
  },

  '7': {
    title: 'Business Proposal',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Business Proposal</title><style>${baseStyles}</style></head><body>
    <div class="header"><div><h1>BUSINESS PROPOSAL</h1><div style="font-size:11px;color:#6b7280">Prepared for: {{CLIENT_NAME}} &nbsp;|&nbsp; {{PROPOSAL_DATE}}</div></div><div style="text-align:right;font-size:11px;color:#6b7280">{{YOUR_COMPANY}}<br>{{YOUR_ADDRESS}}</div></div>
    <h2>Executive Summary</h2>
    <div class="highlight">{{PROBLEM_STATEMENT}}<br><br>Total investment: <b>{{TOTAL_INVESTMENT}}</b></div>
    <h2>Problem Statement</h2>
    <p>{{PROBLEM_STATEMENT}}</p>
    <h2>Proposed Solution</h2>
    <p>{{PROPOSED_SOLUTION}}</p>
    <h2>Pricing &amp; Investment</h2>
    <table>
      <tr><th>Service</th><th>Description</th><th>Investment</th></tr>
      <tr><td>Systems Audit &amp; Strategy</td><td>Technical assessment, roadmap</td><td>Included</td></tr>
      <tr><td>Implementation</td><td>Full setup and configuration</td><td>Included</td></tr>
      <tr><td>Training &amp; Support</td><td>6 months post-launch support</td><td>Included</td></tr>
      <tr class="total-row"><td colspan="2">Total Investment</td><td>{{TOTAL_INVESTMENT}}</td></tr>
    </table>
    <h2>Why Choose Us?</h2>
    <p>Experienced team | Dedicated project manager | Proven track record | Client satisfaction guaranteed.</p>
    <h2>Next Steps</h2>
    <p>To move forward: 1. Sign this proposal &amp; return. 2. Pay 25% deposit ({{DEPOSIT_AMOUNT}}). 3. Kickoff meeting scheduled within 5 business days.</p>
    <p style="font-size:11px;color:#6b7280">This proposal is valid for 30 days from {{PROPOSAL_DATE}}.</p>
    <div class="sig-block">
      <div><div class="sig-line">{{YOUR_COMPANY}} — Authorized Signature</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
      <div><div class="sig-line">{{CLIENT_NAME}} — Acceptance Signature</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
    </div>
    </body></html>`,
  },

  '8': {
    title: 'Freelance Contract',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Freelance Contract</title><style>${baseStyles}</style></head><body>
    <div class="header"><div><h1>FREELANCE WORK AGREEMENT</h1><div style="font-size:11px;color:#6b7280">#{{CONTRACT_NUMBER}} &nbsp;|&nbsp; {{CONTRACT_DATE}}</div></div></div>
    <p>This Agreement is between <b>{{FREELANCER_NAME}}</b> ("Freelancer") and <b>{{CLIENT_NAME}}</b> ("Client"), entered {{CONTRACT_DATE}}.</p>
    <h2>1. Services &amp; Deliverables</h2>
    <p>{{DELIVERABLES}}</p>
    <h2>2. Payment Terms</h2>
    <div class="highlight"><b>Project Total: {{PROJECT_TOTAL}}</b><br>50% upfront ({{DEPOSIT_AMOUNT}}) upon signing &nbsp;|&nbsp; Remaining 50% upon final delivery<br>Payment via bank transfer. Late payments incur 2% monthly fee.</div>
    <h2>3. Intellectual Property</h2>
    <p>All rights transfer to Client upon receipt of final payment in full. Freelancer retains portfolio rights unless otherwise agreed in writing.</p>
    <h2>4. Independent Contractor</h2>
    <p>Freelancer is an independent contractor, not an employee. Freelancer is responsible for own taxes, insurance, and equipment.</p>
    <h2>5. Revision Policy</h2>
    <p>Additional revisions beyond the agreed scope are billed at {{HOURLY_RATE}}. Client must provide written feedback within 5 business days of each submission.</p>
    <h2>6. Termination</h2>
    <p>Client may cancel with 7 days written notice. Client owes payment for all work completed. Non-refundable deposit is retained.</p>
    <div class="sig-block">
      <div><div class="sig-line">Freelancer: {{FREELANCER_NAME}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
      <div><div class="sig-line">Client: {{CLIENT_NAME}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
    </div>
    </body></html>`,
  },

  '9': {
    title: 'Expense Report',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Expense Report</title><style>${baseStyles}</style></head><body>
    <div class="header"><div><h1>EXPENSE REPORT</h1><div style="font-size:11px;color:#6b7280">Period: {{REPORT_PERIOD}}</div></div>
    <div style="text-align:right;font-size:11px;color:#6b7280"><b>Employee:</b> {{EMPLOYEE_NAME}}<br><b>Dept:</b> {{DEPARTMENT}} &nbsp;|&nbsp; <b>ID:</b> {{EMPLOYEE_ID}}<br><b>Manager:</b> {{MANAGER_NAME}}</div></div>
    <table>
      <tr><th>Date</th><th>Category</th><th>Description</th><th>Receipt</th><th>Amount</th></tr>
      <tr><td>___/___/____</td><td>Travel</td><td>Flight — Destination (client visit)</td><td>Yes</td><td>$___</td></tr>
      <tr><td>___/___/____</td><td>Accommodation</td><td>Hotel — nights</td><td>Yes</td><td>$___</td></tr>
      <tr><td>___/___/____</td><td>Meals</td><td>Client dinner</td><td>Yes</td><td>$___</td></tr>
      <tr><td>___/___/____</td><td>Ground Transport</td><td>Uber / Taxi</td><td>Yes</td><td>$___</td></tr>
      <tr><td>___/___/____</td><td>Office Supplies</td><td>Presentation materials</td><td>Yes</td><td>$___</td></tr>
      <tr class="total-row"><td colspan="4" style="text-align:right">TOTAL</td><td>$___</td></tr>
    </table>
    <div style="display:flex;gap:32px;margin-top:24px">
      <div class="highlight" style="flex:1"><b>Subtotal:</b> $___<br><b>Company Card Charges:</b> -$___<br><b>Amount to Reimburse:</b> <b>$___</b></div>
      <div style="flex:1;font-size:12px"><b>Purpose of Travel/Expense:</b><br>{{EXPENSE_PURPOSE}}</div>
    </div>
    <div class="sig-block" style="margin-top:32px">
      <div><div class="sig-line">Employee: {{EMPLOYEE_NAME}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
      <div><div class="sig-line">Manager Approval: {{MANAGER_NAME}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
      <div><div class="sig-line">Finance Approval</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
    </div>
    </body></html>`,
  },

  '10': {
    title: 'Performance Review',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Performance Review</title><style>${baseStyles}
    .rating-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 12px; }
    .stars { display: flex; gap: 4px; }
    .star { width: 18px; height: 18px; border: 1.5px solid #d1d5db; border-radius: 2px; display: inline-block; }
    .star.filled { background: #1a1a1a; border-color: #1a1a1a; }
    .section-box { border: 1px solid #e5e7eb; border-radius: 6px; padding: 14px; margin-bottom: 14px; }
    </style></head><body>
    <div class="header"><div><h1>PERFORMANCE REVIEW</h1><div style="font-size:11px;color:#6b7280">Annual Review — FY {{REVIEW_YEAR}}</div></div>
    <div style="text-align:right;font-size:11px;color:#6b7280">HR Department</div></div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px">
      <div><span class="label">Employee Name</span><br><span class="value">{{EMPLOYEE_NAME}}</span></div>
      <div><span class="label">Position</span><br><span class="value">{{POSITION}}</span></div>
      <div><span class="label">Department</span><br><span class="value">{{DEPARTMENT}}</span></div>
      <div><span class="label">Review Period</span><br><span class="value">Jan {{REVIEW_YEAR}} – Dec {{REVIEW_YEAR}}</span></div>
      <div><span class="label">Manager</span><br><span class="value">{{MANAGER_NAME}}</span></div>
      <div><span class="label">Review Date</span><br><span class="value">{{REVIEW_DATE}}</span></div>
    </div>
    <h2>Performance Ratings</h2>
    <p style="font-size:11px;color:#6b7280">Scale: 1 = Needs Improvement &nbsp; 2 = Developing &nbsp; 3 = Meets Expectations &nbsp; 4 = Exceeds &nbsp; 5 = Outstanding</p>
    ${[['Job Knowledge & Technical Skills', 4], ['Quality of Work', 4], ['Productivity & Efficiency', 3], ['Communication Skills', 5], ['Teamwork & Collaboration', 4], ['Initiative & Innovation', 4], ['Customer / Client Focus', 5], ['Time Management', 3]].map(([label, r]) => `
    <div class="rating-row"><span>${label}</span><div class="stars">${[1,2,3,4,5].map(i => `<div class="star ${i <= (r as number) ? 'filled' : ''}"></div>`).join('')}</div><span style="font-size:12px;font-weight:600;min-width:20px;text-align:right">${r}/5</span></div>`).join('')}
    <h2>Goals &amp; Achievements</h2>
    <div class="section-box"><p style="font-size:12px;margin:0">{{GOALS_ACHIEVED}}</p></div>
    <h2>Manager Comments</h2>
    <div class="section-box" style="min-height:60px;color:#374151;font-size:12px">{{MANAGER_COMMENTS}}</div>
    <h2>Development Plan — Next 12 Months</h2>
    <div class="section-box" style="font-size:12px">{{DEVELOPMENT_PLAN}}</div>
    <div class="sig-block">
      <div><div class="sig-line">Manager: {{MANAGER_NAME}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
      <div><div class="sig-line">Employee: {{EMPLOYEE_NAME}}</div><div style="font-size:11px;color:#6b7280;margin-top:4px">Date: _______</div></div>
    </div>
    </body></html>`,
  },

  '11': {
    title: 'Sales Invoice Bundle',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Sales Invoice Bundle</title><style>${baseStyles}</style></head><body>
    <div class="header"><div><h1>SALES INVOICE BUNDLE</h1><div style="font-size:11px;color:#6b7280">5-Template Pack &nbsp;|&nbsp; ${today}</div></div><div style="text-align:right;font-size:11px;color:#6b7280">{{YOUR_COMPANY}}<br>{{YOUR_EMAIL}}<br>{{YOUR_PHONE}}<br>{{YOUR_ADDRESS}}</div></div>
    <p>This bundle includes 5 professional invoice styles. Use as standalone documents — each is print-ready.</p>
    <h2>Bundle Contents</h2>
    <table>
      <tr><th>#</th><th>Template</th><th>Best For</th></tr>
      <tr><td>1</td><td>Classic Professional Invoice</td><td>Consulting, services</td></tr>
      <tr><td>2</td><td>Modern Minimal Invoice</td><td>Freelancers, agencies</td></tr>
      <tr><td>3</td><td>Itemized Product Invoice</td><td>Retail, wholesale</td></tr>
      <tr><td>4</td><td>Recurring Subscription Invoice</td><td>SaaS, subscriptions</td></tr>
      <tr><td>5</td><td>Pro Services Invoice with Hours</td><td>Legal, accounting, consulting</td></tr>
    </table>
    <h2>Sample Invoice — Classic Style</h2>
    <div class="header" style="margin-top:16px"><div><div style="font-size:24px;font-weight:800">INVOICE #001</div><div style="font-size:11px;color:#6b7280">Date: ${today} &nbsp;|&nbsp; Due: Net 30</div></div>
    <div style="text-align:right;font-size:11px;color:#6b7280">{{YOUR_COMPANY}}<br>{{YOUR_EMAIL}} &nbsp;|&nbsp; {{YOUR_PHONE}}</div></div>
    <table>
      <tr><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr>
      <tr><td>Product / Service</td><td>1</td><td>$____</td><td>$____</td></tr>
      <tr><td>Product / Service</td><td>1</td><td>$____</td><td>$____</td></tr>
      <tr class="total-row"><td colspan="3" style="text-align:right">TOTAL</td><td>$____</td></tr>
    </table>
    <p style="margin-top:24px;font-size:12px;color:#6b7280">To use: Replace placeholder text with your company info and line items. Save as PDF when ready to send.</p>
    </body></html>`,
  },

  '12': {
    title: 'Meeting Agenda',
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Meeting Agenda</title><style>${baseStyles}
    .time-block { width: 80px; min-width: 80px; color: #6b7280; font-size: 11px; font-weight: 600; }
    .agenda-item { display: flex; align-items: flex-start; padding: 10px 4px; border-bottom: 1px solid #f3f4f6; }
    .presenter { font-size: 10px; background: #f3f4f6; padding: 2px 8px; border-radius: 10px; margin-left: 8px; white-space: nowrap; }
    </style></head><body>
    <div class="header">
      <div><h1>MEETING AGENDA</h1><div style="font-size:11px;color:#6b7280">{{MEETING_TITLE}}</div></div>
      <div style="text-align:right;font-size:11px;color:#6b7280"><b>Date:</b> {{MEETING_DATE}}<br><b>Time:</b> {{MEETING_TIME}}<br><b>Location:</b> {{MEETING_LOCATION}}<br><b>Facilitator:</b> {{FACILITATOR}}</div>
    </div>
    <h2>Attendees</h2>
    <p style="font-size:12px">{{ATTENDEES}}</p>
    <h2>Objectives</h2>
    <div class="highlight">{{MEETING_OBJECTIVES}}</div>
    <h2>Agenda</h2>
    <div class="agenda-item"><div class="time-block">00:00–00:05</div><div><b>Welcome &amp; Housekeeping</b><br><span style="font-size:12px;color:#6b7280">Zoom link, recording notice, agenda review</span></div><span class="presenter">{{FACILITATOR}}</span></div>
    <div class="agenda-item"><div class="time-block">00:05–00:25</div><div><b>Topic 1 — Overview</b><br><span style="font-size:12px;color:#6b7280">Key results, metrics, highlights</span></div><span class="presenter">Presenter</span></div>
    <div class="agenda-item"><div class="time-block">00:25–00:45</div><div><b>Topic 2 — Deep Dive</b><br><span style="font-size:12px;color:#6b7280">Analysis, discussion, Q&amp;A</span></div><span class="presenter">Presenter</span></div>
    <div class="agenda-item"><div class="time-block">00:45–00:55</div><div><b>Open Discussion</b><br><span style="font-size:12px;color:#6b7280">Questions, blockers, alignment</span></div><span class="presenter">All</span></div>
    <div class="agenda-item"><div class="time-block">00:55–01:00</div><div><b>Action Items &amp; Wrap-Up</b><br><span style="font-size:12px;color:#6b7280">Confirm owners, due dates, next meeting</span></div><span class="presenter">{{FACILITATOR}}</span></div>
    <h2>Action Items</h2>
    <table>
      <tr><th>Action</th><th>Owner</th><th>Due Date</th><th>Status</th></tr>
      <tr><td>Follow-up item 1</td><td>___</td><td>___</td><td>Pending</td></tr>
      <tr><td>Follow-up item 2</td><td>___</td><td>___</td><td>Pending</td></tr>
    </table>
    <h2>Notes</h2>
    <p style="border:1px solid #e5e7eb;padding:16px;border-radius:6px;min-height:80px;font-size:12px;color:#9ca3af">Meeting notes...</p>
    </body></html>`,
  },
};

// ─── Default values ──────────────────────────────────────────────────────

export const TEMPLATE_DEFAULTS: Record<string, Record<string, string>> = {
  '1': {
    YOUR_COMPANY: 'Your Company Name',
    YOUR_ADDRESS: '123 Business Ave, New York, NY 10001',
    YOUR_EMAIL: 'billing@yourcompany.com',
    YOUR_PHONE: '(555) 000-0000',
    INVOICE_NUMBER: `INV-${year}-001`,
    INVOICE_DATE: today,
    DUE_DATE: dueDate,
    CLIENT_NAME: 'Client Name',
    CLIENT_ADDRESS: '456 Client St, City, State ZIP',
    CLIENT_EMAIL: 'client@email.com',
    PAYMENT_INFO: 'Bank Transfer: [Your Bank] | Account: XXXX | Routing: XXXX',
  },
  '2': {
    PROVIDER_NAME: 'Your Company Name',
    PROVIDER_ADDRESS: '123 Business Ave, City, State ZIP',
    PROVIDER_EMAIL: 'contact@yourcompany.com',
    CLIENT_NAME: 'Client Name',
    CLIENT_ADDRESS: '456 Client St, City, State ZIP',
    CONTRACT_NUMBER: `SVC-${year}-001`,
    CONTRACT_DATE: today,
    SERVICES_DESC: 'Professional services as mutually agreed upon.',
    MONTHLY_RATE: '$0.00',
    CONTRACT_TERM: '12 months',
    HOURLY_RATE: '$0/hr',
  },
  '3': {
    FULL_NAME: 'Your Full Name',
    JOB_TITLE: 'Your Professional Title',
    EMAIL: 'your@email.com',
    PHONE: '(555) 000-0000',
    LOCATION: 'City, State',
    LINKEDIN: 'linkedin.com/in/yourname',
    GITHUB: '',
    JOB1_TITLE: 'Your Job Title',
    JOB1_COMPANY: 'Company Name',
    JOB1_DATES: `${year - 2} – Present`,
    JOB1_BULLETS: 'Key accomplishment or responsibility here\nAnother achievement with measurable results',
    EDUCATION: 'Degree — University Name',
    EDUCATION_DATES: `${year - 6} – ${year - 2}`,
    SKILLS: 'Skill 1, Skill 2, Skill 3, Skill 4',
  },
  '4': {
    DISCLOSING_PARTY: 'Disclosing Party Name',
    RECEIVING_PARTY: 'Receiving Party Name',
    AGREEMENT_DATE: today,
    NDA_TERM: '3 years',
    SURVIVAL_TERM: '2 years',
    GOVERNING_STATE: 'New York',
    PURPOSE: 'Evaluating a potential business relationship.',
  },
  '5': {
    COMPANY_NAME: 'Company Name',
    COMPANY_ADDRESS: '123 Business Ave, City, State ZIP',
    HR_EMAIL: 'hr@company.com',
    CANDIDATE_NAME: 'Candidate Name',
    POSITION_TITLE: 'Job Title',
    DEPARTMENT: 'Department',
    MANAGER_NAME: 'Hiring Manager',
    WORK_LOCATION: 'City, State',
    START_DATE: 'TBD',
    BASE_SALARY: '$0',
    SIGNING_BONUS: 'N/A',
    BONUS_PERCENT: '0%',
    OFFER_DATE: today,
    OFFER_EXPIRY: dueDate,
  },
  '6': {
    PROJECT_NAME: 'Project Name',
    PROJECT_MANAGER: 'Project Manager',
    TARGET_DATE: 'TBD',
    PROJECT_DATE: today,
  },
  '7': {
    YOUR_COMPANY: 'Your Company Name',
    YOUR_ADDRESS: '123 Business Ave, City, State',
    CLIENT_NAME: 'Client Name',
    PROPOSAL_DATE: today,
    TOTAL_INVESTMENT: '$0',
    DEPOSIT_AMOUNT: '$0',
    PROBLEM_STATEMENT: 'Describe the client\'s current challenges here.',
    PROPOSED_SOLUTION: 'Describe your proposed solution here.',
  },
  '8': {
    FREELANCER_NAME: 'Freelancer Name',
    CLIENT_NAME: 'Client Name',
    CONTRACT_DATE: today,
    CONTRACT_NUMBER: `FWA-${year}-001`,
    PROJECT_TOTAL: '$0',
    DEPOSIT_AMOUNT: '$0',
    HOURLY_RATE: '$0/hr',
    DELIVERABLES: 'List deliverables here.',
  },
  '9': {
    EMPLOYEE_NAME: 'Employee Name',
    DEPARTMENT: 'Department',
    EMPLOYEE_ID: 'EMP-0001',
    MANAGER_NAME: 'Manager Name',
    REPORT_PERIOD: `${today}`,
    EXPENSE_PURPOSE: 'Business travel and client meetings.',
  },
  '10': {
    EMPLOYEE_NAME: 'Employee Name',
    POSITION: 'Job Title',
    DEPARTMENT: 'Department',
    MANAGER_NAME: 'Manager Name',
    REVIEW_YEAR: `${year}`,
    REVIEW_DATE: today,
    GOALS_ACHIEVED: 'List goals and achievements here.',
    MANAGER_COMMENTS: 'Manager comments here.',
    DEVELOPMENT_PLAN: 'Development goals for next year.',
  },
  '11': {
    YOUR_COMPANY: 'Your Company Name',
    YOUR_EMAIL: 'your@email.com',
    YOUR_PHONE: '(555) 000-0000',
    YOUR_ADDRESS: '123 Main St, City, State ZIP',
  },
  '12': {
    MEETING_TITLE: 'Meeting Title',
    MEETING_DATE: today,
    MEETING_TIME: '10:00 AM – 11:00 AM',
    MEETING_LOCATION: 'Conference Room / Video Call',
    FACILITATOR: 'Facilitator Name',
    ATTENDEES: 'List attendees here',
    MEETING_OBJECTIVES: '1. First objective\n2. Second objective',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────

const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Fill a template with user-provided field values.
 * Falls back to TEMPLATE_DEFAULTS for any missing field.
 */
export const fillTemplate = (templateId: string, inputData: Record<string, string>): TemplateContent => {
  const template = TEMPLATE_CONTENT[templateId];
  if (!template) throw new Error(`Template not found: ${templateId}`);

  const defaults = TEMPLATE_DEFAULTS[templateId] || {};
  let data = { ...inputData };

  // ── Special pre-processing: Invoice line items ──────────────────────────
  if (templateId === '1') {
    const rows: string[] = [];
    let subtotal = 0;

    for (let i = 1; i <= 4; i++) {
      const desc = data[`ITEM${i}_DESC`]?.trim();
      if (!desc) continue;
      const qtyStr = data[`ITEM${i}_QTY`]?.trim() || '1';
      const priceRaw = parseFloat((data[`ITEM${i}_PRICE`] || '0').replace(/[^0-9.]/g, '')) || 0;
      const qtyNum = parseFloat(qtyStr.replace(/[^0-9.]/g, '')) || 1;
      const amount = qtyNum * priceRaw;
      subtotal += amount;
      rows.push(
        `<tr><td>${escapeHtml(desc)}</td><td>${escapeHtml(qtyStr)}</td><td>$${priceRaw.toFixed(2)}</td><td>$${amount.toFixed(2)}</td></tr>`
      );
    }

    const taxRate = parseFloat((data['TAX_RATE'] || '0').replace(/[^0-9.]/g, '')) || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    data['INVOICE_ROWS'] = rows.length > 0
      ? rows.join('')
      : '<tr><td colspan="4" style="text-align:center;color:#9ca3af;padding:12px">No line items added</td></tr>';
    data['SUBTOTAL'] = `$${subtotal.toFixed(2)}`;
    data['TAX_RATE_DISPLAY'] = taxRate > 0 ? `${taxRate}%` : '0%';
    data['TAX_AMOUNT'] = `$${taxAmount.toFixed(2)}`;
    data['TOTAL_DUE'] = `$${total.toFixed(2)}`;
  }

  let html = template.html;

  html = html.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    const raw = data[key] !== undefined ? data[key] : (defaults[key] || '');

    if (!raw) return '';

    if (key.endsWith('_BULLETS')) {
      return raw
        .split('\n')
        .map((b) => b.trim())
        .filter(Boolean)
        .map((b) => `<li>${escapeHtml(b)}</li>`)
        .join('');
    }

    if (key === 'SKILLS') {
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => `<span class="skill-tag">${escapeHtml(s)}</span>`)
        .join('');
    }

    // INVOICE_ROWS is already HTML — don't escape it
    if (key === 'INVOICE_ROWS') return raw;

    if (raw.includes('\n')) {
      return raw.split('\n').map(escapeHtml).join('<br>');
    }

    return escapeHtml(raw);
  });

  return { title: template.title, html };
};
